/**
 * 🙈 BLIND DATE MODE - Matcher sans voir les photos !
 * 
 * Concept révolutionnaire :
 * - On cache les photos au début
 * - On montre uniquement la bio, les passions, l'accroche
 * - Après 10 messages échangés : REVEAL ! 🎉
 * 
 * Pourquoi ça buzze :
 * - C'est CONTRE le système (anti-superficiel)
 * - Les médias en parlent
 * - "L'app qui te fait matcher sur la personnalité, pas le physique"
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Eye, EyeOff, Lock, Unlock, Heart, MessageCircle, 
  Sparkles, Star, Zap, Gift, X, Check
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { GuideAvatar } from '../../soul/GuideAvatar';

// 🎯 Composant carte Blind Date (photo cachée)
export function BlindDateCard({ 
  profile, 
  messagesCount = 0, 
  onLike, 
  onPass, 
  onMessage,
  isDarkMode 
}) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [revealProgress, setRevealProgress] = useState(0);
  
  const MESSAGES_TO_REVEAL = 10;
  
  // Calculer la progression vers le reveal
  useEffect(() => {
    const progress = Math.min((messagesCount / MESSAGES_TO_REVEAL) * 100, 100);
    setRevealProgress(progress);
    
    // Déclencher le reveal automatique
    if (messagesCount >= MESSAGES_TO_REVEAL && !isRevealed) {
      triggerReveal();
    }
  }, [messagesCount, isRevealed]);
  
  // 🎉 Déclencher la révélation
  const triggerReveal = () => {
    setIsRevealed(true);
    
    // Confettis !
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#EC4899', '#8B5CF6', '#F59E0B']
    });
    
    toast.success('🎉 REVEAL ! Découvre à quoi ressemble ton match !', {
      duration: 5000
    });
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`relative rounded-3xl overflow-hidden ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      } shadow-2xl`}
    >
      {/* Zone photo (floue ou révélée) */}
      <div className="relative h-80 overflow-hidden">
        {profile?.photos?.[0] ? (
          <img
            src={profile.photos[0]}
            alt=""
            className={`w-full h-full object-cover transition-all duration-1000 ${
              isRevealed ? '' : 'blur-3xl scale-110'
            }`}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-600" />
        )}
        
        {/* Overlay mystère si pas révélé */}
        {!isRevealed && (
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/80 to-pink-900/80 flex flex-col items-center justify-center">
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="mb-4"
            >
              <GuideAvatar guide="pixie" expression="wink" size="lg" />
            </motion.div>
            <h3 className="text-white text-xl font-bold mb-2">Blind Date Mode</h3>
            <p className="text-white/70 text-sm text-center px-8">
              Découvre la personnalité avant le physique !
            </p>
            
            {/* Barre de progression */}
            <div className="w-48 mt-6">
              <div className="flex justify-between text-xs text-white/60 mb-1">
                <span>💬 {messagesCount} messages</span>
                <span>{MESSAGES_TO_REVEAL} pour reveal</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-pink-400 to-yellow-400"
                  initial={{ width: 0 }}
                  animate={{ width: `${revealProgress}%` }}
                />
              </div>
            </div>
            
            {/* Badge mystère */}
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/30 backdrop-blur-sm flex items-center gap-1">
              <EyeOff className="w-4 h-4 text-white" />
              <span className="text-white text-xs font-medium">Mystère</span>
            </div>
          </div>
        )}
        
        {/* Badge révélé */}
        {isRevealed && (
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-green-500 flex items-center gap-1">
            <Eye className="w-4 h-4 text-white" />
            <span className="text-white text-xs font-medium">Révélé !</span>
          </div>
        )}
      </div>
      
      {/* Infos profil */}
      <div className="p-5">
        {/* Nom (toujours visible) */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {profile?.name || 'Mystère'}, {profile?.age || '??'}
            </h2>
            {profile?.city && (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                📍 {profile.city}
              </p>
            )}
          </div>
          {profile?.isVerified && (
            <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-600 text-xs font-medium">
              ✓ Vérifié
            </span>
          )}
        </div>
        
        {/* Accroche (toujours visible - c'est le point fort !) */}
        {profile?.tagline && (
          <p className={`text-sm italic mb-4 ${isDarkMode ? 'text-pink-300' : 'text-pink-600'}`}>
            "{profile.tagline}"
          </p>
        )}
        
        {/* Passions (toujours visibles) */}
        {profile?.passions && (
          <div className="flex flex-wrap gap-1 mb-4">
            {Object.values(profile.passions).flat().slice(0, 5).map((passion, idx) => (
              <span
                key={idx}
                className={`px-2 py-1 rounded-full text-xs ${
                  isDarkMode 
                    ? 'bg-purple-500/20 text-purple-300' 
                    : 'bg-purple-100 text-purple-600'
                }`}
              >
                {passion}
              </span>
            ))}
          </div>
        )}
        
        {/* Bio (toujours visible) */}
        {profile?.bio && (
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {profile.bio}
          </p>
        )}
        
        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="lg"
            onClick={onPass}
            className="flex-1 rounded-full"
          >
            <X className="w-5 h-5 mr-1" />
            Passer
          </Button>
          <Button
            size="lg"
            onClick={onMessage}
            className="flex-1 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white"
          >
            <MessageCircle className="w-5 h-5 mr-1" />
            Discuter
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// 🎉 Animation de révélation
export function RevealMoment({ profile, onContinue, isDarkMode }) {
  const [stage, setStage] = useState(0);
  
  useEffect(() => {
    // Animation séquentielle
    const timers = [
      setTimeout(() => setStage(1), 500),
      setTimeout(() => setStage(2), 1500),
      setTimeout(() => setStage(3), 2500),
    ];
    
    return () => timers.forEach(clearTimeout);
  }, []);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
    >
      <div className="text-center">
        {/* Stage 0: Suspense */}
        {stage === 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-8xl"
          >
            🧨
          </motion.div>
        )}
        
        {/* Stage 1: Compte à rebours */}
        {stage === 1 && (
          <motion.div
            initial={{ scale: 2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <p className="text-white text-xl mb-4">Et voici...</p>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.5, repeat: 3 }}
              className="text-6xl text-white font-bold"
            >
              3... 2... 1...
            </motion.div>
          </motion.div>
        )}
        
        {/* Stage 2: Reveal */}
        {stage === 2 && (
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', bounce: 0.5 }}
          >
            <p className="text-yellow-400 text-2xl font-bold mb-4">🎉 REVEAL !</p>
            <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-yellow-400 shadow-2xl mx-auto">
              {profile?.photos?.[0] ? (
                <img 
                  src={profile.photos[0]} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-4xl text-white font-bold">
                  {profile?.name?.[0] || '?'}
                </div>
              )}
            </div>
            <p className="text-white text-xl font-bold mt-4">
              {profile?.name}, {profile?.age}
            </p>
          </motion.div>
        )}
        
        {/* Stage 3: CTA */}
        {stage === 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-pink-500 shadow-2xl mx-auto mb-6">
              {profile?.photos?.[0] ? (
                <img 
                  src={profile.photos[0]} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-4xl text-white font-bold">
                  {profile?.name?.[0] || '?'}
                </div>
              )}
            </div>
            <p className="text-white text-xl font-bold mb-2">
              {profile?.name}, {profile?.age}
            </p>
            <p className="text-white/70 mb-6">
              Vous avez échangé 10 messages avant de vous voir ! 💜
            </p>
            <Button
              size="lg"
              onClick={onContinue}
              className="bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full px-8"
            >
              <Heart className="w-5 h-5 mr-2" />
              Continuer la discussion
            </Button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// 🎮 Panel Blind Date Mode
export function BlindDatePanel({ 
  profiles = [], 
  onLike, 
  onPass, 
  onStartChat,
  isDarkMode 
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showExplanation, setShowExplanation] = useState(true);
  
  const currentProfile = profiles[currentIndex];
  
  const handlePass = () => {
    if (currentIndex < profiles.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    onPass && onPass(currentProfile);
  };
  
  const handleMessage = () => {
    onStartChat && onStartChat(currentProfile);
  };
  
  if (showExplanation) {
    return (
      <div className={`rounded-3xl p-6 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="mb-4 flex justify-center"
        >
          <GuideAvatar guide="pixie" expression="wink" size="lg" />
        </motion.div>
        <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Blind Date Mode
        </h2>
        <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Découvre la personnalité avant le physique !<br/>
          Les photos sont cachées jusqu'à ce que vous échangiez <strong>10 messages</strong>.
        </p>
        
        <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
          <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
            💡 Pourquoi c'est génial ?
          </h3>
          <ul className={`text-sm text-left space-y-1 ${isDarkMode ? 'text-purple-200' : 'text-purple-600'}`}>
            <li>• Finis les jugements superficiels</li>
            <li>• Connexion authentique d'abord</li>
            <li>• Surprise garantie au reveal !</li>
            <li>• Plus de conversations profondes</li>
          </ul>
        </div>
        
        <Button
          size="lg"
          onClick={() => setShowExplanation(false)}
          className="bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full px-8"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          C'est parti !
        </Button>
      </div>
    );
  }
  
  if (!currentProfile) {
    return (
      <div className={`rounded-3xl p-8 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
        <span className="text-6xl mb-4 block">💜</span>
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Plus de profils pour l'instant
        </h3>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
          Reviens plus tard, de nouvelles personnes arrivent ! ✨
        </p>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GuideAvatar guide="pixie" expression="default" size="xs" />
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Blind Date
          </h3>
        </div>
        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {currentIndex + 1} / {profiles.length}
        </span>
      </div>
      
      {/* Carte */}
      <BlindDateCard
        profile={currentProfile}
        messagesCount={currentProfile.messagesExchanged || 0}
        onPass={handlePass}
        onMessage={handleMessage}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}

export default BlindDatePanel;
